// A small view layer: JSX → virtual nodes → keyed DOM patching, plus a global redraw.
// Components are plain functions of props; shared state lives in stores that call redraw().

export type Child = VNode | string | number | boolean | null | undefined | Child[];

export interface VNode {
  tag: string;
  props: Record<string, any>;
  children: VNode[];
  key?: string | number;
  text?: string;
  dom?: Node;
}

const TEXT = "#text";
const SVG_NS = "http://www.w3.org/2000/svg";

function textNode(s: string): VNode {
  return { tag: TEXT, props: {}, children: [], text: s };
}

function flatten(children: Child[], out: VNode[] = []): VNode[] {
  for (const c of children) {
    if (c === null || c === undefined || c === false || c === true) continue;
    if (Array.isArray(c)) flatten(c, out);
    else if (typeof c === "object") out.push(c);
    else out.push(textNode(String(c)));
  }
  return out;
}

export function h(tag: string | ((props: any) => Child), props: Record<string, any> | null, ...children: Child[]): any {
  const p = props ?? {};
  if (typeof tag === "function") return tag({ ...p, children: flatten(children) });
  return { tag, props: p, children: flatten(children), key: p.key };
}

export const Fragment = (p: { children: VNode[] }) => p.children;

// ───────────── DOM creation & patching ─────────────

function setStyle(el: HTMLElement, prev: any, next: any) {
  if (typeof next === "string" || typeof prev === "string") {
    el.style.cssText = typeof next === "string" ? next : "";
    if (typeof next !== "object" || !next) return;
    prev = {};
  }
  prev = prev ?? {};
  next = next ?? {};
  for (const k in prev) if (!(k in next)) k.startsWith("--") ? el.style.removeProperty(k) : ((el.style as any)[k] = "");
  for (const k in next) {
    if (prev[k] === next[k]) continue;
    if (k.startsWith("--")) el.style.setProperty(k, next[k]);
    else (el.style as any)[k] = next[k];
  }
}

function listener(this: any, e: Event) {
  return this.__ev[e.type](e);
}

function setProp(el: any, key: string, prev: any, next: any, svg: boolean) {
  if (key === "key" || key === "ref" || key === "children") return;
  if (key === "style") return setStyle(el, prev, next);
  if (key[0] === "o" && key[1] === "n") {
    const type = key.slice(2).toLowerCase();
    el.__ev ??= {};
    if (next) {
      if (!el.__ev[type]) el.addEventListener(type, listener);
      el.__ev[type] = next;
    } else if (el.__ev[type]) {
      el.removeEventListener(type, listener);
      delete el.__ev[type];
    }
    return;
  }
  // Form properties are compared against the previous virtual value, so text a person is
  // typing is never overwritten unless the app deliberately changes the value.
  if (!svg && (key === "value" || key === "checked" || key === "selected" || key === "indeterminate")) {
    if (prev !== next || el[key] !== next) {
      if (prev !== next) el[key] = next ?? (key === "value" ? "" : false);
    }
    return;
  }
  const attr = key === "className" ? "class" : key === "htmlFor" ? "for" : key;
  if (next === null || next === undefined || next === false) el.removeAttribute(attr);
  else el.setAttribute(attr, next === true ? "" : String(next));
}

function create(v: VNode, svg: boolean): Node {
  if (v.tag === TEXT) return (v.dom = document.createTextNode(v.text!));
  const isSvg = svg || v.tag === "svg";
  const el = isSvg ? document.createElementNS(SVG_NS, v.tag) : document.createElement(v.tag);
  const isSelect = v.tag === "select";
  for (const k in v.props) if (!(isSelect && k === "value")) setProp(el, k, undefined, v.props[k], isSvg);
  const childSvg = isSvg && v.tag !== "foreignObject";
  for (const c of v.children) el.appendChild(create(c, childSvg));
  // A <select> can only take its value once its options exist.
  if (isSelect && "value" in v.props) setProp(el, "value", undefined, v.props.value, false);
  v.dom = el;
  if (v.props.ref) v.props.ref(el);
  return el;
}

function sameKind(a: VNode, b: VNode) {
  return a.tag === b.tag && a.key === b.key;
}

/** Updates `old`'s DOM to match `next` and returns the node to use (reused or new). */
function morph(old: VNode, next: VNode, svg: boolean): Node {
  if (!sameKind(old, next)) return create(next, svg);
  const el = (next.dom = old.dom!);
  if (next.tag === TEXT) {
    if (old.text !== next.text) el.nodeValue = next.text!;
    return el;
  }
  const isSvg = svg || next.tag === "svg";
  const keys = new Set([...Object.keys(old.props), ...Object.keys(next.props)]);
  for (const k of keys) if (old.props[k] !== next.props[k]) setProp(el, k, old.props[k], next.props[k], isSvg);
  patchChildren(el as Element, old.children, next.children, isSvg && next.tag !== "foreignObject");
  // When a <select>'s options arrive after it was drawn, apply its value again.
  if (next.tag === "select" && "value" in next.props) {
    const optionsChanged = old.children.length !== next.children.length ||
      old.children.some((c, i) => c.props?.value !== next.children[i]?.props?.value);
    if (optionsChanged && (el as HTMLSelectElement).value !== next.props.value) (el as HTMLSelectElement).value = next.props.value ?? "";
  }
  if (next.props.ref && next.props.ref !== old.props.ref) next.props.ref(el);
  return el;
}

function patchChildren(parent: Element, oldCh: VNode[], newCh: VNode[], svg: boolean) {
  const keyed = new Map<string | number, VNode>();
  const unkeyed: VNode[] = [];
  for (const c of oldCh) (c.key !== undefined ? keyed.set(c.key, c) : unkeyed.push(c));
  let u = 0;
  const reused = new Set<Node>();
  const doms: Node[] = newCh.map((n) => {
    let o: VNode | undefined;
    if (n.key !== undefined) {
      o = keyed.get(n.key);
      if (o) keyed.delete(n.key);
    } else {
      o = unkeyed[u];
      if (o) u++;
    }
    if (!o) return create(n, svg);
    const d = morph(o, n, svg);
    if (d === o.dom) reused.add(d);
    return d;
  });
  for (const o of oldCh) if (o.dom && !reused.has(o.dom) && o.dom.parentNode === parent) parent.removeChild(o.dom);
  for (let i = 0; i < doms.length; i++) {
    const cur = parent.childNodes[i];
    if (cur !== doms[i]) parent.insertBefore(doms[i], cur ?? null);
  }
}

// ───────────── Mounting & redraw ─────────────

let rootEl: Element | null = null;
let rootView: (() => VNode) | null = null;
let rootTree: VNode | null = null;
let scheduled = false;
const afterDraw: Array<() => void> = [];

export function mount(el: Element, view: () => VNode) {
  rootEl = el;
  rootView = view;
  el.textContent = "";
  draw();
}

function draw() {
  scheduled = false;
  if (!rootEl || !rootView) return;
  const next = rootView();
  if (!rootTree) rootEl.appendChild(create(next, false));
  else {
    const d = morph(rootTree, next, false);
    if (d !== rootTree.dom) rootEl.replaceChild(d, rootTree.dom!);
  }
  rootTree = next;
  const fns = afterDraw.splice(0);
  for (const fn of fns) fn();
}

/** Schedules a re-render of the whole app on the next microtask. */
export function redraw() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(draw);
}

/** Runs once after the next render (e.g. to move focus). */
export function onNextDraw(fn: () => void) {
  afterDraw.push(fn);
}

// ───────────── State helpers ─────────────

/** A mutable object whose changes trigger a redraw. */
export function store<T extends object>(initial: T): T & { set(patch: Partial<T>): void } {
  const s = initial as T & { set(patch: Partial<T>): void };
  s.set = (patch: Partial<T>) => {
    Object.assign(s, patch);
    redraw();
  };
  return s;
}

const locals = new Map<string, any>();

/** Per-screen state that survives redraws. Reset with `resetLocal(key)` (e.g. on navigation). */
export function local<T extends object>(key: string, init: () => T): T & { set(patch: Partial<T>): void } {
  let s = locals.get(key);
  if (!s) {
    s = store(init());
    locals.set(key, s);
  }
  return s;
}

export function resetLocal(prefix: string) {
  for (const k of [...locals.keys()]) if (k.startsWith(prefix)) locals.delete(k);
}

// ───────────── Async data ─────────────

export interface Resource<T> {
  data: T | undefined;
  error: any;
  loading: boolean;
  reload(): Promise<void>;
}

const resources = new Map<string, Resource<any>>();

/** Loads data once per key and redraws when it arrives. `invalidate(prefix)` refetches. */
export function resource<T>(key: string, loader: () => Promise<T>): Resource<T> {
  let r = resources.get(key) as Resource<T> | undefined;
  if (r) return r;
  const res: Resource<T> = {
    data: undefined,
    error: null,
    loading: true,
    async reload() {
      res.loading = true;
      redraw();
      try {
        res.data = await loader();
        res.error = null;
      } catch (e) {
        res.error = e;
      }
      res.loading = false;
      redraw();
    },
  };
  resources.set(key, res);
  void res.reload();
  return res;
}

export function invalidate(prefix: string) {
  for (const [k, r] of resources) if (k.startsWith(prefix)) void r.reload();
}

export function dropResources(prefix = "") {
  for (const k of [...resources.keys()]) if (k.startsWith(prefix)) resources.delete(k);
}
